// Shared persistence for editor mode documents (the locked A <-> B interface):
// loads the project's document for a mode on entry, exposes it as state, and
// debounce-saves edits wholesale through the docs store. Each mode owns its
// data shape; an empty stored document falls back to the mode's initial value.
// Pending saves are flushed on unmount and on tab close so the debounce never
// discards work.
import { useCallback, useEffect, useRef, useState } from 'react';

import { loadModeDocument, saveModeDocument, type ModeDocKind } from '../services/docs';
import { createDebouncedSaver, type DebouncedSaver } from './debouncedSave';
import { useEditorHost } from './EditorHost';
import { useToast } from '../design/toast';

const SAVE_DEBOUNCE_MS = 1500;

const MODE_LABELS: Record<ModeDocKind, string> = {
  logic: 'Logic',
  hud: 'HUD',
  audio: 'Audio',
  fx: 'FX',
  gamedata: 'Game Data',
  pixel: 'Pixel Studio',
  model: 'Model Studio',
  gamesetup: 'Game Setup',
  prefabs: 'Prefabs',
  cutscenes: 'Cutscenes',
  introsequences: 'Intro Sequences',
};

export interface ModeDocState<T> {
  /** The document data; the fallback until the stored copy arrives. */
  data: T;
  /** Replace the data and schedule a save. */
  setData: (next: T | ((current: T) => T)) => void;
  /** Immediately flush any pending debounced save. Call before navigating away. */
  flush: () => void;
  /** True until the stored document has been loaded (or load failed). */
  loading: boolean;
  /** Last save failure, if any; cleared by the next successful save. */
  error: string | null;
}

export function useModeDocument<T extends Record<string, unknown>>(
  kind: ModeDocKind,
  fallback: T,
): ModeDocState<T> {
  const { projectId } = useEditorHost();
  const [data, setDataState] = useState<T>(fallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef<T>(fallback);
  const fallbackRef = useRef(fallback);
  const saverRef = useRef<DebouncedSaver<T> | null>(null);
  const notify = useToast();
  const label = MODE_LABELS[kind] ?? kind;
  // True once the user edits. A load that resolves after the first edit must
  // NOT overwrite that edit - the async response is stale by then.
  const touchedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    // New (project, kind): the previous document's edits no longer apply, so
    // accept this load.
    touchedRef.current = false;
    setLoading(true);
    loadModeDocument<T>(projectId, kind)
      .then((doc) => {
        if (cancelled || touchedRef.current) return;
        // An unsaved document arrives as empty data; keep the mode's initial value.
        const stored = doc.data && Object.keys(doc.data).length > 0 ? doc.data : fallbackRef.current;
        latest.current = stored;
        setDataState(stored);
      })
      .catch(() => {
        // Offline or unreachable: the mode still works on its initial value,
        // but silently - a save from here would previously have clobbered
        // whatever was actually stored. Make the risk visible instead.
        if (cancelled) return;
        notify(`Couldn't load your saved ${label} data - you're starting from a blank ${label} and a save here may overwrite what was stored. Reload once you're back online.`, 'error');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, kind, label, notify]);

  // One saver per (project, kind). Leaving the mode, switching project or
  // hiding/closing the tab flushes any pending save instead of dropping it;
  // teardown saves go out with keepalive so the request outlives the page.
  useEffect(() => {
    const saver = createDebouncedSaver<T>((value, { keepalive }) => {
      saveModeDocument(projectId, kind, value, { keepalive })
        .then(() => setError(null))
        .catch((err: unknown) => {
          const message = err instanceof Error ? err.message : 'Save failed.';
          setError(message);
          notify(`${label} didn't save: ${message}`, 'error');
        });
    }, SAVE_DEBOUNCE_MS);
    saverRef.current = saver;
    const onPageHide = () => saver.flush(true);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') saver.flush(true);
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      saver.flush();
      if (saverRef.current === saver) saverRef.current = null;
    };
  }, [projectId, kind, label, notify]);

  const setData = useCallback((next: T | ((current: T) => T)) => {
    const resolved = typeof next === 'function' ? (next as (current: T) => T)(latest.current) : next;
    touchedRef.current = true;
    latest.current = resolved;
    setDataState(resolved);
    saverRef.current?.schedule(resolved);
  }, []);

  const flush = useCallback(() => {
    saverRef.current?.flush();
  }, []);

  return { data, setData, flush, loading, error };
}
